/**
 * Tenants Index Page
 *
 * Lists all tenants with search, filter, and pagination.
 *
 * @fileoverview Admin tenants list page
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  useDisclosure,
  useToast,
  Icon,
} from '@chakra-ui/react';
import { MdAdd } from 'react-icons/md';
import {
  TenantTable,
  TenantFilters,
  DeleteTenantModal,
} from '../../../components/admin';
import { useAdminTenants } from '../../../hooks/useAdminTenants';
import { adminApi } from '../../../services/adminApi';
import { useSupportMode } from '../../../shared/support-mode';
import type { AdminTenant } from '../../../types/admin';
import { toUserMessage } from '../../../lib/apiError';

/**
 * Tenants Index Page Component
 *
 * Main page for managing tenants.
 */
export const TenantsIndex: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const deleteModal = useDisclosure();
  const { enterSupportMode } = useSupportMode();

  const [selectedTenant, setSelectedTenant] = useState<AdminTenant | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const {
    tenants,
    loading,
    pagination,
    filters,
    setPage,
    setLimit,
    setSearch,
    setIsActive,
    setSort,
    resetFilters,
    refetch,
  } = useAdminTenants();

  // Handle view tenant
  const handleView = (tenant: AdminTenant) => {
    navigate(`/admin/tenants/${tenant.id}`);
  };

  // Handle support mode
  const handleSupportMode = (tenant: AdminTenant) => {
    enterSupportMode({
      id: tenant.id,
      name: tenant.name,
      slug: tenant.slug,
      managerEmail: tenant.managerEmail,
      isActive: tenant.isActive,
      createdAt: tenant.createdAt,
    });
    navigate('/home');
  };

  // Handle edit tenant
  const handleEdit = (tenant: AdminTenant) => {
    navigate(`/admin/tenants/${tenant.id}/edit`);
  };

  // Handle delete tenant
  const handleDeleteClick = (tenant: AdminTenant) => {
    setSelectedTenant(tenant);
    deleteModal.onOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTenant) return;

    setDeleteLoading(true);
    try {
      await adminApi.tenants.delete(selectedTenant.id);
      toast({
        title: 'Tenant deleted',
        description: `${selectedTenant.name} has been deleted.`,
        status: 'success',
        duration: 3000,
      });
      deleteModal.onClose();
      refetch();
    } catch (error) {
      toast({
        title: 'Something went wrong',
        description: toUserMessage(error, "We couldn't delete the tenant. Please try again."),
        status: 'error',
        duration: 5000,
      });
    } finally {
      setDeleteLoading(false);
      setSelectedTenant(null);
    }
  };

  // Handle activate tenant
  const handleActivate = async (tenant: AdminTenant) => {
    try {
      await adminApi.tenants.activate(tenant.id);
      toast({
        title: 'Tenant activated',
        description: `${tenant.name} is now active.`,
        status: 'success',
        duration: 3000,
      });
      refetch();
    } catch (error) {
      toast({
        title: 'Something went wrong',
        description: toUserMessage(error, "We couldn't activate the tenant. Please try again."),
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Handle deactivate tenant
  const handleDeactivate = async (tenant: AdminTenant) => {
    try {
      await adminApi.tenants.deactivate(tenant.id);
      toast({
        title: 'Tenant deactivated',
        description: `${tenant.name} is now inactive.`,
        status: 'warning',
        duration: 3000,
      });
      refetch();
    } catch (error) {
      toast({
        title: 'Something went wrong',
        description: toUserMessage(error, "We couldn't deactivate the tenant. Please try again."),
        status: 'error',
        duration: 5000,
      });
    }
  };

  return (
    <VStack spacing={3} align="stretch">
      {/* Header — one compact row instead of a two-line title block */}
      <HStack justify="space-between" wrap="wrap" gap={3}>
        <HStack spacing={2} align="baseline">
          <Heading size="md">Tenants</Heading>
          <Text color="gray.500" fontSize="sm">
            Manage all tenants in the system
          </Text>
        </HStack>
        <Button
          size="sm"
          colorScheme="blue"
          leftIcon={<Icon as={MdAdd} />}
          onClick={() => navigate('/admin/tenants/new')}
        >
          Create Tenant
        </Button>
      </HStack>

      {/* Filters */}
      <TenantFilters
        search={filters.search || ''}
        isActive={filters.isActive}
        onSearchChange={setSearch}
        onIsActiveChange={setIsActive}
        onReset={resetFilters}
      />

      {/* Table */}
      <TenantTable
        tenants={tenants}
        loading={loading}
        pagination={pagination}
        onPageChange={setPage}
        onPageSizeChange={setLimit}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        onActivate={handleActivate}
        onDeactivate={handleDeactivate}
        onSupportMode={handleSupportMode}
        sortBy={filters.sortBy}
        sortOrder={filters.sortOrder}
        onSort={setSort}
      />

      {/* Delete Modal */}
      <DeleteTenantModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.onClose}
        tenant={selectedTenant}
        loading={deleteLoading}
        onConfirm={handleDeleteConfirm}
      />
    </VStack>
  );
};

export default TenantsIndex;
